import { useCallback, useEffect, useRef, useState } from "react";
import {
  Activity, CheckCircle2, AlertCircle, AlertTriangle, RefreshCcw,
  CreditCard, MessageSquare, Mail, Database, Server, Clock, Cpu, HardDrive, Loader2,
} from "lucide-react";
import { api, formatDateTimeBR } from "../../lib/api";

const REFRESH_MS = 30000;

const CHECK_META = {
  payment_gateway: { label: "Mercado Pago", icon: CreditCard },
  payment_webhook: { label: "Webhook de Pagamento", icon: CreditCard },
  zapi: { label: "Z-API (WhatsApp)", icon: MessageSquare },
  resend: { label: "Resend (E-mail)", icon: Mail },
  mongodb: { label: "MongoDB", icon: Database },
  background_jobs: { label: "Background Jobs", icon: Server },
  scheduler: { label: "Agendador", icon: Clock },
  backup: { label: "Backup MongoDB", icon: HardDrive },
  resources: { label: "Recursos do Servidor", icon: Cpu },
};

const STATUS_BADGE = {
  ok: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "OK", Icon: CheckCircle2 },
  warning: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Atenção", Icon: AlertTriangle },
  error: { cls: "bg-red-100 text-red-800 border-red-200", label: "Erro", Icon: AlertCircle },
};

const OVERALL_BG = {
  ok: "bg-emerald-50 border-emerald-200",
  warning: "bg-amber-50 border-amber-200",
  error: "bg-red-50 border-red-200",
};

function StatusBadge({ status }) {
  const m = STATUS_BADGE[status] || STATUS_BADGE.warning;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${m.cls}`} data-testid={`status-badge-${status}`}>
      <m.Icon size={12} /> {m.label}
    </span>
  );
}

function CheckCard({ keyName, check }) {
  const meta = CHECK_META[keyName] || { label: keyName, icon: Activity };
  const Icon = meta.icon;
  const details = check?.details || {};
  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col gap-3"
      data-testid={`health-card-${keyName}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-[#0A1128]/5 flex items-center justify-center">
            <Icon size={18} className="text-[#0A1128]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{meta.label}</div>
            <div className="text-sm font-semibold text-[#0A1128] leading-tight">{check?.message || "—"}</div>
          </div>
        </div>
        <StatusBadge status={check?.status || "warning"} />
      </div>
      {Object.keys(details).length > 0 && (
        <div className="border-t border-gray-100 pt-3 grid grid-cols-2 gap-2 text-xs text-gray-600" data-testid={`health-details-${keyName}`}>
          {Object.entries(details).slice(0, 6).map(([k, v]) => (
            <div key={k} className="flex flex-col">
              <span className="text-[9px] uppercase tracking-wider text-gray-400">{k}</span>
              <span className="font-mono text-[11px] text-gray-700 truncate" title={typeof v === "object" ? JSON.stringify(v) : String(v)}>
                {v == null ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityList({ title, items, testid, render }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid={testid}>
      <h3 className="text-sm font-bold text-[#0A1128] mb-3 flex items-center gap-2">
        <Activity size={14} className="text-[#D4AF37]" /> {title}
      </h3>
      {(!items || items.length === 0) ? (
        <div className="text-xs text-gray-400 italic">Nenhum evento recente</div>
      ) : (
        <ul className="divide-y divide-gray-100" data-testid={`${testid}-list`}>
          {items.slice(0, 10).map((it, i) => (
            <li key={i} className="py-2 text-xs">{render(it)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SystemHealth() {
  const [diag, setDiag] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const [d, a] = await Promise.all([
        api.get("/admin/system/diagnostics"),
        api.get("/admin/system/recent-activity"),
      ]);
      setDiag(d.data);
      setActivity(a.data);
      setLastUpdate(new Date());
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Falha ao carregar diagnóstico");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="health-loading">
        <Loader2 className="animate-spin text-[#0A1128]" size={28} />
      </div>
    );
  }

  const checks = diag?.checks || {};
  const overall = diag?.overall || "warning";
  const overallMeta = STATUS_BADGE[overall];

  return (
    <div className="space-y-6" data-testid="system-health-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[#0A1128] font-light">Painel de Saúde do Sistema</h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitoramento em tempo real de todas as integrações e subsistemas.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <div className="text-[10px] text-gray-400 uppercase tracking-wider" data-testid="health-last-update">
              Atualizado às {lastUpdate.toLocaleTimeString("pt-BR")}
            </div>
          )}
          <button
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-2 bg-[#0A1128] text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#14213D] disabled:opacity-60"
            data-testid="health-refresh-btn"
          >
            <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} />
            Atualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700" data-testid="health-error">
          {error}
        </div>
      )}

      {/* Overall status */}
      <div className={`rounded-2xl border p-6 ${OVERALL_BG[overall]}`} data-testid="health-overall">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <overallMeta.Icon size={32} className={
              overall === "ok" ? "text-emerald-600" :
              overall === "error" ? "text-red-600" : "text-amber-600"
            } />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Status Geral</div>
              <div className="text-2xl font-black text-[#0A1128] leading-none">
                {overall === "ok" ? "Tudo operacional" : overall === "error" ? "Falha crítica" : "Atenção necessária"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-gray-600">
            <span className="px-2 py-1 rounded bg-white/60 border border-gray-200 uppercase font-bold tracking-wider">
              Ambiente: <span className="text-[#0A1128]">{diag?.environment || "—"}</span>
            </span>
            <span className="px-2 py-1 rounded bg-white/60 border border-gray-200 uppercase font-bold tracking-wider">
              Versão: <span className="text-[#0A1128]">{diag?.version || "—"}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Check cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="health-checks-grid">
        {Object.entries(checks).map(([k, v]) => (
          <CheckCard key={k} keyName={k} check={v} />
        ))}
      </div>

      {/* Recent activity */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2">
          <Clock size={14} /> Atividade Recente
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ActivityList
            title="Webhooks Mercado Pago"
            items={activity?.payment_webhooks}
            testid="activity-payment-webhooks"
            render={(it) => (
              <div className="flex justify-between items-center gap-3">
                <span className="font-mono text-[10px] text-gray-500 truncate max-w-[60%]">
                  {it.event || it.event_type || it.action || "—"}
                </span>
                <span className="text-[10px] text-gray-400">{formatDateTimeBR(it.received_at)}</span>
              </div>
            )}
          />
          <ActivityList
            title="Webhooks Z-API"
            items={activity?.zapi_webhooks}
            testid="activity-zapi-webhooks"
            render={(it) => (
              <div className="flex justify-between items-center gap-3">
                <span className="font-mono text-[10px] text-gray-500 truncate max-w-[60%]">
                  {it.type || it.event || "—"}
                </span>
                <span className="text-[10px] text-gray-400">{formatDateTimeBR(it.received_at)}</span>
              </div>
            )}
          />
          <ActivityList
            title="E-mails enviados"
            items={activity?.emails}
            testid="activity-emails"
            render={(it) => (
              <div className="flex justify-between items-center gap-3">
                <div className="truncate max-w-[60%]">
                  <div className="text-[11px] font-semibold text-[#0A1128] truncate">{it.subject || it.template || "—"}</div>
                  <div className="text-[10px] text-gray-500 truncate">{it.to || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-gray-400">{formatDateTimeBR(it.sent_at)}</div>
                  <div className={`text-[9px] uppercase font-bold ${it.status === "sent" ? "text-emerald-600" : it.status === "failed" ? "text-red-600" : "text-amber-600"}`}>
                    {it.status || "—"}
                  </div>
                </div>
              </div>
            )}
          />
          <ActivityList
            title="Mensagens WhatsApp"
            items={activity?.whatsapps}
            testid="activity-whatsapps"
            render={(it) => (
              <div className="flex justify-between items-center gap-3">
                <div className="truncate max-w-[60%]">
                  <div className="text-[11px] font-semibold text-[#0A1128] truncate">{it.kind || it.template || "—"}</div>
                  <div className="text-[10px] text-gray-500 truncate">{it.phone || it.to || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-gray-400">{formatDateTimeBR(it.sent_at)}</div>
                  <div className={`text-[9px] uppercase font-bold ${it.status === "sent" ? "text-emerald-600" : it.status === "failed" ? "text-red-600" : "text-amber-600"}`}>
                    {it.status || "—"}
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <div className="text-center text-[10px] text-gray-400 uppercase tracking-widest pt-4 pb-8">
        <HardDrive size={10} className="inline mr-1" />
        Atualização automática a cada {REFRESH_MS / 1000}s
      </div>
    </div>
  );
}
